/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import CampaignModel from 'cio/models/campaign';
import { queryEncode } from 'cio/helpers/query';
import ApiService from 'cio/services/api';
import DS from 'ember-data';

enum Tabs {
  nextToTrigger = 'next_to_trigger',
  testWithPerson = 'test_with_person',
}

interface CampaignsTriggerSetupDatePreviewArgs {
  campaign: CampaignModel;
  attribute: string;
  frequency: string;
  leadDays: number;
  startTime: string;
  zone: string;
  fallbackZone: string;
  showNextToTrigger: boolean;
  hasUnsavedAttributes: boolean;
}

export default class CampaignsTriggerSetupDatePreview extends Component<CampaignsTriggerSetupDatePreviewArgs> {
  @service api!: ApiService;
  @service store!: DS.Store;

  @tracked selectedTab = this.args.showNextToTrigger ? Tabs.nextToTrigger : Tabs.testWithPerson;
  @tracked nextToTriggerCount = 0;
  previousAttribute = '';
  readonly tabs = Tabs;

  get people() {
    return this.fetchPeopleWithAttributeTask.last?.value;
  }

  get attribute() {
    return this.args.attribute.split(/\?|\.|\[/).shift();
  }

  // Used to show a warning regarding the people count when using nested attributes. See https://github.com/customerio/issues/issues/8459 for details.
  get isNestedAttribute() {
    return /\?|\.|\[/.test(this.args.attribute);
  }

  fetchPeopleWithAttributeTask = restartableTask(async () => {
    try {
      let conditions = [{ field: this.attribute, operator: 'exists', type: 'attribute' }];
      return await this.store.query('customer', { filters: queryEncode(conditions) });
    } catch (err) {
      return [];
    }
  });

  fetchNextPeopleTask = restartableTask(async (page: number) => {
    let { customers, meta } = await this.api.request(`campaigns/${this.args.campaign.id}/next?from=${page}`);

    this.nextToTriggerCount = meta?.pagination?.total;
    return customers;
  });

  // When this component receives new attributes, we reset the state and re-fetch some data.
  // If the attribute selected changed, we need to fetch all the people with the changed attribute.
  resetStateTask = restartableTask(async () => {
    if (this.args.hasUnsavedAttributes) {
      this.selectedTab = Tabs.testWithPerson;
    }

    if (this.previousAttribute !== this.args.attribute) {
      await this.fetchPeopleWithAttributeTask.perform();
      this.previousAttribute = this.args.attribute;
    }
  });

  @action
  changeTab(tab: Tabs) {
    this.selectedTab = tab;
  }
}
